import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { apiContractRequest } from '../../../../api/client';

const names={local:'Local',production:'Producción',paused:'Pausado'};
const descriptions={
 CURRENT_CONNECTED:'WhatsApp está conectado en este backend.',
 CURRENT_NOT_CONNECTED:'Este backend está retomando la conexión.',
 CURRENT_ON_OTHER_INSTANCE:'WhatsApp está conectado en otra instancia del mismo código.',
 UNATTESTED_LEGACY_HAS_LEASE:'Una instancia anterior conserva la conexión.',
 OUTDATED_RUNTIME_HAS_LEASE:'Una instancia con código anterior conserva la conexión.',
 NO_ACTIVE_LEASE:'Esperando que el backend elegido abra la conexión.',
 NOT_CONFIGURED:'Guardá primero el proveedor de WhatsApp.',
 RUNTIME_SWITCH_PENDING:'Traspaso en curso. La conexión anterior se está liberando automáticamente.',
};

export default function OwnerRuntimeAuthority(){
 const [status,setStatus]=useState(null),[target,setTarget]=useState('local'),[revision,setRevision]=useState(0);
 const [error,setError]=useState(''),[notice,setNotice]=useState(''),[busy,setBusy]=useState(false);
 const dirty=useRef(false),request=useRef(0),pendingRead=useRef(0);
 const reload=useCallback(async(force=false)=>{
  if(pendingRead.current&&!force)return;
  const sequence=++request.current;
  pendingRead.current=sequence;
  try{
   const data=await apiContractRequest('/api/owner/whatsapp/baileys/status',{retryAttempts:0});
   if(sequence!==request.current)return;
   const next=data?.runtimeAuthority;
   setStatus(next||null);
   if(!dirty.current&&next?.execution){setTarget(next.execution.target);setRevision(next.execution.revision);}
   setError(next?.execution?.source==='mongodb'?'':'Este backend todavía no tiene instalado el selector de runtime en MongoDB.');
  }catch(err){if(sequence===request.current)setError(err?.message||'No pude consultar el runtime de WhatsApp.');}
  finally{if(pendingRead.current===sequence)pendingRead.current=0;}
 },[]);
 useEffect(()=>{reload();const timer=setInterval(reload,5000);return()=>{clearInterval(timer);request.current++;};},[reload]);
 const save=async()=>{
  if(busy)return;
  setBusy(true);setError('');setNotice('');request.current++;
  try{
   const result=await apiContractRequest('/api/owner/whatsapp/baileys/runtime',{
    method:'PUT',body:{target,expectedRevision:revision},retryAttempts:0,
   });
   dirty.current=false;
   setNotice(target==='paused'?'Runtime pausado. Se conserva la sesión de WhatsApp.':
    `Runtime guardado en ${names[target]}. ${result?.switching?'El traspaso es automático; esperá alrededor de un minuto.':'El backend elegido retomará el seguimiento si está encendido.'}`);
   await reload(true);
  }catch(err){setError(err?.message||'No pude guardar el runtime.');}
  finally{setBusy(false);}
 };
 const handoff=async()=>{
  if(!status?.requiresHandoff||busy)return;
  if(!window.confirm('Se va a recuperar la conexión que conserva una versión anterior. Se mantiene la sesión y no se reenvían mensajes. ¿Continuar?'))return;
  setBusy(true);setError('');
  try{
   await apiContractRequest('/api/owner/whatsapp/baileys/runtime/handoff',{
    method:'POST',body:{confirm:'FENCE_LEGACY_OWNER_RUNTIME'},retryAttempts:0,
   });
   setNotice('Recuperación solicitada. Revisá el estado de la conexión en unos segundos.');await reload(true);
  }catch(err){setError(err?.message||'No pude recuperar la conexión anterior.');}
  finally{setBusy(false);}
 };
 const execution=status?.execution,compatible=execution?.source==='mongodb';
 const label=execution?.target==='paused'?'Pausado':execution?.active?'Activo aquí':`Asignado a ${names[execution?.target]||'…'}`;
 return <Box sx={{mb:2,p:1.5,border:'1px solid',borderColor:'divider',borderRadius:2}}>
  <Stack direction={{xs:'column',sm:'row'}} justifyContent="space-between" alignItems={{sm:'center'}} gap={1}>
   <Typography fontWeight={750} variant="subtitle2">Runtime de WhatsApp</Typography>
   <Chip size="small" color={execution?.active?'success':'default'} label={label}/>
  </Stack>
  <Typography variant="body2" sx={{mt:.8,mb:1}}>Elegí dónde querés que el bot conecte WhatsApp, responda y haga el seguimiento. La elección se comparte entre ambos backends y se conserva al reiniciarlos.</Typography>
  <Stack direction={{xs:'column',sm:'row'}} gap={1} alignItems={{sm:'center'}}>
   <TextField select size="small" label="Dónde ejecutar WhatsApp" value={target} disabled={busy||!compatible}
    onChange={event=>{dirty.current=true;setTarget(event.target.value);}} sx={{minWidth:210}}>
    <MenuItem value="local">Local · pruebas</MenuItem>
    <MenuItem value="production">Producción</MenuItem>
    <MenuItem value="paused">Pausado</MenuItem>
   </TextField>
   <Button variant="contained" onClick={save} disabled={busy||!compatible||status?.status==='NOT_CONFIGURED'}>{busy?<CircularProgress size={18}/>: 'Guardar runtime'}</Button>
   <Button variant="outlined" onClick={()=>{dirty.current=false;reload(true);}} disabled={busy}>Actualizar</Button>
  </Stack>
  {execution&&<Typography variant="caption" sx={{display:'block',mt:1}}>Este backend: {names[execution.environment]||'sin identificar'} · Elegido: {names[execution.target]||'sin identificar'}.</Typography>}
  {status?.provider==='baileys'&&<Typography variant="caption" sx={{display:'block',mt:.5}}>{descriptions[status?.status]||'Consultando la conexión…'}</Typography>}
  {execution?.target==='local'&&<Typography variant="caption" sx={{display:'block',mt:.5}}>En Local, el seguimiento funciona mientras tu backend local esté encendido. Para seguir con la computadora apagada, elegí Producción.</Typography>}
  {status?.requiresHandoff&&execution?.active&&<Button size="small" color="warning" onClick={handoff} disabled={busy} sx={{mt:1}}>Recuperar conexión anterior</Button>}
  {error&&<Alert severity="warning" sx={{mt:1}}>{error}</Alert>}
  {notice&&<Alert severity="info" sx={{mt:1}}>{notice}</Alert>}
 </Box>;
}
